import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, Trash2, Users, User, Search, ChevronUp, ChevronDown, Pencil, 
  Mail, Phone, Link2, X, Check
} from "lucide-react";
import { searchParishMembers } from "@/lib/api";
import { toast } from "sonner";

const STAFF_ROLES = [
  { value: "pastor", label: "Pastor / Parish Priest" },
  { value: "parochial_vicar", label: "Parochial Vicar / Associate Pastor" },
  { value: "deacon", label: "Deacon" },
  { value: "religious", label: "Religious (Sister/Brother)" },
  { value: "staff", label: "Parish Staff" },
];

const TITLE_SUGGESTIONS = {
  pastor: ["Pastor", "Fr.", "Father", "Rev."],
  parochial_vicar: ["Parochial Vicar", "Fr.", "Father", "Associate Pastor", "Rev."],
  deacon: ["Deacon", "Dcn."],
  religious: ["Sister", "Sr.", "Brother", "Br."],
  staff: ["Parish Secretary", "Director", "Coordinator", "Music Director", "DRE", "Youth Minister", "Business Manager"],
};

function MemberSearchDialog({ isOpen, onClose, onSelect, parishId }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
    }
  }, [isOpen]);

  const handleSearch = async () => {
    if (!query.trim() || query.length < 2) return;
    
    setLoading(true);
    try {
      const res = await searchParishMembers(parishId, query);
      setResults(res.data.members || []);
    } catch (err) {
      toast.error("Failed to search members");
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Link to Parish Member
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name or email..."
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
            />
            <Button 
              onClick={handleSearch} 
              disabled={loading}
              className="bg-[var(--liturgical-primary)] text-white"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
          
          {results.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-2">
              {results.map((member) => (
                <div
                  key={member.user_id}
                  onClick={() => onSelect(member)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E0D5] hover:bg-[#F9F7F2] cursor-pointer"
                >
                  <Avatar className="h-10 w-10">
                    {member.profile_picture ? (
                      <AvatarImage src={member.profile_picture} alt={member.name} />
                    ) : (
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                        {member.name?.charAt(0)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-[#2D2A26] font-['Nunito']">{member.name}</p>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">{member.email}</p>
                  </div>
                  <Check className="w-4 h-4 text-green-600" />
                </div>
              ))}
            </div>
          )}
          
          {results.length === 0 && query && !loading && (
            <p className="text-center text-sm text-[#6B665E] font-['Nunito'] py-4">
              No members found. They must be a member of your parish to link.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaffMemberDialog({ isOpen, onClose, onSave, staffMember, parishId }) {
  const [formData, setFormData] = useState({
    role: "",
    title: "",
    name: "",
    user_id: null,
    email: "",
    phone: "",
  });
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  const [linkedMember, setLinkedMember] = useState(null);

  useEffect(() => {
    if (staffMember) {
      setFormData({
        role: staffMember.role || "",
        title: staffMember.title || "",
        name: staffMember.name || "",
        user_id: staffMember.user_id || null,
        email: staffMember.email || "",
        phone: staffMember.phone || "",
      });
      if (staffMember.user_id && staffMember.linked_member) {
        setLinkedMember(staffMember.linked_member);
      }
    } else {
      setFormData({
        role: "",
        title: "",
        name: "",
        user_id: null,
        email: "",
        phone: "",
      });
      setLinkedMember(null);
    }
  }, [staffMember, isOpen]);

  const handleSelectMember = (member) => {
    setLinkedMember(member);
    setFormData({
      ...formData,
      user_id: member.user_id,
      name: formData.name || member.name,
      email: member.email || formData.email,
    });
    setShowMemberSearch(false);
  };

  const handleUnlinkMember = () => {
    setLinkedMember(null);
    setFormData({ ...formData, user_id: null });
  };

  const handleSubmit = () => {
    if (!formData.role) {
      toast.error("Please select a role");
      return;
    }
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    onSave({
      ...formData,
      linked_member: linkedMember
    });
    onClose();
  };

  const titleSuggestions = TITLE_SUGGESTIONS[formData.role] || [];

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
              {staffMember ? "Edit Staff Member" : "Add Staff Member"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Role */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm">
                  <SelectValue placeholder="Select role..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {STAFF_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value} className="font-['Nunito']">
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Title */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Pastor, Fr., Deacon"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                />
              </div>
              {titleSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {titleSuggestions.map(suggestion => (
                    <Badge
                      key={suggestion}
                      variant="outline"
                      className="cursor-pointer text-xs border-[#E5E0D5] hover:bg-[#F9F7F2]"
                      onClick={() => setFormData({...formData, title: suggestion})}
                    >
                      {suggestion}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* Name */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Full name"
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
              />
            </div>
            
            {/* Link to Member */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Link to App Member (Optional)</Label>
              {linkedMember ? (
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                  <Avatar className="h-8 w-8">
                    {linkedMember.profile_picture ? (
                      <AvatarImage src={linkedMember.profile_picture} alt={linkedMember.name} />
                    ) : (
                      <AvatarFallback className="bg-green-600 text-white text-xs">
                        {linkedMember.name?.charAt(0)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-800 font-['Nunito']">{linkedMember.name}</p>
                    <p className="text-xs text-green-600 font-['Nunito']">Linked member</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleUnlinkMember}
                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMemberSearch(true)}
                  className="w-full border-[#E5E0D5] font-['Nunito'] text-sm text-[#6B665E]"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Search & Link Member
                </Button>
              )}
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Email"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Phone"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1 border-[#E5E0D5] font-['Nunito']">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="flex-1 bg-[var(--liturgical-primary)] text-white font-['Nunito']">
                {staffMember ? "Save Changes" : "Add Staff Member"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <MemberSearchDialog
        isOpen={showMemberSearch}
        onClose={() => setShowMemberSearch(false)}
        onSelect={handleSelectMember}
        parishId={parishId}
      />
    </>
  );
}

export default function ParishStaffSection({ staff = [], onChange, parishId }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (staffMember) => {
    if (editingIndex !== null) {
      // Edit existing
      const newStaff = [...staff];
      newStaff[editingIndex] = staffMember;
      onChange(newStaff);
    } else {
      // Add new
      onChange([...staff, staffMember]);
    }
  };

  const handleRemove = (index) => {
    const newStaff = staff.filter((_, i) => i !== index);
    onChange(newStaff);
  };

  const handleMove = (index, direction) => {
    const newStaff = [...staff];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newStaff.length) return;
    
    [newStaff[index], newStaff[targetIndex]] = [newStaff[targetIndex], newStaff[index]];
    onChange(newStaff);
  };

  const getRoleLabel = (role) => {
    return STAFF_ROLES.find(r => r.value === role)?.label || role;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'pastor': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'parochial_vicar': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'deacon': return 'bg-green-100 text-green-700 border-green-200';
      case 'religious': return 'bg-pink-100 text-pink-700 border-pink-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <Card className="bg-white border-[#E5E0D5] shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Users className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Parish Staff & Clergy
          </CardTitle>
          <Button 
            size="sm" 
            onClick={handleAdd}
            className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-xs"
          >
            <Plus className="w-3 h-3 mr-1" /> Add Staff
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {staff.length === 0 ? (
          <div className="text-center py-6 text-[#6B665E] font-['Nunito'] text-sm">
            <User className="w-8 h-8 mx-auto mb-2 text-[#E5E0D5]" />
            No staff members added yet. Add priests, deacons, and parish staff.
          </div>
        ) : (
          <div className="space-y-2">
            {staff.map((member, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]"
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleMove(idx, 'up')}
                    disabled={idx === 0}
                    className="h-5 w-5 p-0 text-[#6B665E] hover:text-[#2D2A26] disabled:opacity-30"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleMove(idx, 'down')}
                    disabled={idx === staff.length - 1}
                    className="h-5 w-5 p-0 text-[#6B665E] hover:text-[#2D2A26] disabled:opacity-30"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </div>
                
                {/* Avatar */}
                <Avatar className="h-10 w-10 flex-shrink-0">
                  {member.linked_member?.profile_picture ? (
                    <AvatarImage src={member.linked_member.profile_picture} alt={member.name} />
                  ) : (
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
                      {member.name?.charAt(0)}
                    </AvatarFallback>
                  )}
                </Avatar>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#2D2A26] font-['Nunito']">
                      {member.title && `${member.title} `}{member.name}
                    </span>
                    {member.user_id && (
                      <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">
                        <Link2 className="w-2.5 h-2.5 mr-1" />
                        Linked
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className={`text-[10px] ${getRoleColor(member.role)}`}>
                      {getRoleLabel(member.role)}
                    </Badge>
                    {member.email && (
                      <span className="text-[10px] text-[#6B665E] font-['Nunito'] flex items-center gap-0.5">
                        <Mail className="w-2.5 h-2.5" />
                        {member.email}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleEdit(idx)}
                    className="h-7 w-7 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleRemove(idx)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      <StaffMemberDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        staffMember={editingIndex !== null ? staff[editingIndex] : null}
        parishId={parishId}
      />
    </Card>
  );
}
